// The vocabulary the org-admin gate is built from — one definition, two consumers.
//
// `useTenantAdmin` decides whether someone HAS the console; `adminGate` explains why they DON'T.
// Those two answers have to agree, and until now each kept its own copy of these lists with a
// comment on one saying "Mirrors useTenantAdmin". A comment is not an invariant: drift between them
// produces one of two silent failures, and neither surfaces as an error.
//
//   adminGate stricter than the hook  → the console vanishes and nothing explains it
//   adminGate looser than the hook    → someone with a working console is told it is unavailable
//
// Both of those have already happened for other reasons (ENG-2612, ENG-2661), which is why this is
// worth a shared module rather than a comment.
//
// Kept as a lib module rather than living in the hook: `adminGate` is deliberately pure and free of
// React so its branches are assertable without rendering, and importing a hook would end that.

/** Roles that can administer a tenant. A `member` gets the individual UX only. */
export const ADMIN_ROLES = ["owner", "admin"] as const;

/**
 * Tenant types that qualify for an org-admin surface. Family counts (Colin, 2026-07-13) at a
 * reduced tier; the backend treats it as a managed type since ENG-3245.
 */
export const ADMIN_TENANT_TYPES = ["business", "premium", "family"] as const;

/** Of the qualifying types, those that get the FULL capability set — family has no policy tooling. */
export const FULL_CAPABILITY_TENANT_TYPES = ["business", "premium"] as const;

export const isAdminRole = (role: string | null | undefined): boolean =>
  !!role && (ADMIN_ROLES as readonly string[]).includes(role);

/** ENG-3246: a family's subscription is tied to its Owner, so its admins don't manage billing. */
export const holdsBilling = (role: string | null | undefined, tenantType: string | null | undefined): boolean =>
  tenantType === "family" ? role === "owner" : isAdminRole(role);

/**
 * ENG-3407: the role an IT provider's operator holds in a client organization — not a membership but
 * a grant (ENG-2390), reported as the tier by `/subscription-status` and `GET /me/tenants`. What each
 * tier may do is decided by the backend's `MspTierEnforcement` at the active-tenant seam, and by the
 * routes that refuse a provider outright (billing, managed access, inviting); `deriveTenantAdmin`
 * mirrors that, tier by tier. Most to least privileged.
 */
export const PROVIDER_ROLES = ["msp_admin", "msp_policy", "msp_read"] as const;
export type ProviderRole = (typeof PROVIDER_ROLES)[number];

export const isProviderRole = (role: string | null | undefined): role is ProviderRole =>
  !!role && (PROVIDER_ROLES as readonly string[]).includes(role);

/** Any role that opens the organization's console: an owner or admin, or a provider's operator. */
export const isConsoleRole = (role: string | null | undefined): boolean => isAdminRole(role) || isProviderRole(role);

/**
 * ENG-3407: the members half of `deriveTenantAdmin`'s map, by role alone, for a page that holds the
 * edited organization's status rather than the console's (Members & seats, ENG-3372): an owner, an
 * admin or a provider with Admin access works on members; only a member of the organization invites or
 * resends, since both can add a seat and the server needs a membership for them.
 */
export const actsOnMembersAs = (role: string | null | undefined): boolean => isAdminRole(role) || role === "msp_admin";
export const invitesAs = (role: string | null | undefined): boolean => isAdminRole(role);

/**
 * ENG-3246: in a family, admin is the Owner's grant; a company's admins make admins too.
 * ENG-3854: and nobody grants it while the organization's type is not known yet, which is the case
 * while the console has named it from the roster alone: it could be a family.
 */
export const grantsAdmin = (role: string | null | undefined, tenantType: string | null | undefined): boolean =>
  !!tenantType && (tenantType !== "family" || role === "owner");

/**
 * ENG-4089 (counsel rule A5): on a family, making someone an admin is a request they accept, from their
 * own account, and everyone else is told when they do. Mirrors `adminNeedsAcceptance`.
 */
export const asksBeforeAdmin = (tenantType: string | null | undefined): boolean => tenantType === "family";

/** ENG-4089: whether an invitation may carry admin. Never on a family: invite a member, then ask. */
export const invitesAdmin = (role: string | null | undefined, tenantType: string | null | undefined): boolean =>
  grantsAdmin(role, tenantType) && !asksBeforeAdmin(tenantType);

/** ENG-3246: a family's ownership moves only through Haven until a transfer can move its billing too. */
export const transfersOwnershipInConsole = (tenantType: string | null | undefined): boolean => tenantType !== "family";

export const isAdminTenantType = (tenantType: string | null | undefined): boolean =>
  !!tenantType && (ADMIN_TENANT_TYPES as readonly string[]).includes(tenantType);

export const isFullCapabilityTenantType = (tenantType: string | null | undefined): boolean =>
  !!tenantType && (FULL_CAPABILITY_TENANT_TYPES as readonly string[]).includes(tenantType);
